/**
 * Grid Combat Types
 * Type system for tile-based tactical combat, replacing the compass-rose grid.
 * Reuses APCombatParticipant, skills, dice, conditions from the existing engine.
 */

import type {
  APCombatParticipant,
  CombatActionType,
  CommittedReaction,
  DicePoolPreview,
  IncomingThreat,
  ResolutionResult,
  RoundCommit,
  RoundResolution,
  SkillDefinition,
  Stats,
  MagicDiscipline,
  AvailableReactionSet,
  Stat,
  ReactionType,
} from './types';

// ============================================================================
// TILE POSITION
// ============================================================================

export interface TilePosition {
  x: number;
  y: number;
}

// ============================================================================
// RANGE BANDS (Chebyshev distance on tiles)
// ============================================================================

export type GridRangeBand = 'melee' | 'close' | 'ranged';

/** Melee: Chebyshev ≤ 1 (8 adjacent tiles). Close: ≤ 3. Ranged: beyond. */
export function toGridRangeBand(distance: number): GridRangeBand {
  if (distance <= 1) return 'melee';
  if (distance <= 3) return 'close';
  return 'ranged';
}

// ============================================================================
// GRID ENGINE PHASES
// ============================================================================

/**
 * Phased turn order:
 * 1. player_commit — all players plan actions simultaneously
 * 2. player_resolve — player actions resolve in initiative order
 * 3. enemy_turn — each enemy acts (AI-driven), generates threats
 * 4. reaction — players react to accumulated threats
 * 5. round_end — tick conditions, reset AP, check victory/defeat
 * 6. ended — combat over
 */
export type GridEnginePhase =
  | 'player_commit'
  | 'player_resolve'
  | 'enemy_turn'
  | 'reaction'
  | 'round_end'
  | 'ended';

// ============================================================================
// GRID COMBAT STATE
// ============================================================================

export interface GridCombatEngineState {
  phase: GridEnginePhase;
  roundNumber: number;
  participants: APCombatParticipant[];

  /** Tile positions for each participant (keyed by participant ID) */
  positions: Record<string, TilePosition>;

  /** Map dimensions (reference only; actual GridMap passed to step functions) */
  mapWidth: number;
  mapHeight: number;

  /** Initiative-sorted participant IDs (determines action resolution order) */
  turnOrder: string[];

  /** Simultaneous player commits (all players plan at once) */
  pendingPlayerCommits: Record<string, RoundCommit>;

  /** Threats generated during enemy_turn, waiting for player reactions */
  incomingThreats: Record<string, IncomingThreat[]>;

  /** Full combat log across all rounds */
  combatLog: ResolutionResult[];

  /** Last resolved round's results */
  lastResolution: RoundResolution | null;

  /** Combat outcome (null while ongoing) */
  outcome: 'victory' | 'defeat' | 'fled' | null;

  /** Pre-computed available actions per participant (keyed by participant ID) */
  availableActions: Record<string, GridAvailableAction[]>;

  /** Pre-computed available reactions per participant */
  availableReactions: Record<string, AvailableReactionSet[]>;

  /** Skill cooldown tracking: participantId → skillId → uses remaining */
  skillCooldowns: Record<string, Record<string, number>>;
}

// ============================================================================
// GRID AVAILABLE ACTIONS (pre-computed for UI)
// ============================================================================

export interface GridMoveTarget {
  x: number;
  y: number;
  apCost: number;
  path: TilePosition[];
}

export interface GridActionTarget {
  id: string;
  name: string;
  position: TilePosition;
  rangeBand: GridRangeBand;
  distance: number;
  hasLOS: boolean;
  dicePool?: DicePoolPreview;
}

export interface GridAvailableAction {
  id: string;
  name: string;
  type: CombatActionType;
  apCost: number;
  description: string;
  stat: Stat;
  range: 'melee' | 'close' | 'ranged' | 'self' | 'all';
  resolveCost: number;
  keywords: string[];
  dicePool?: DicePoolPreview;
  targets?: GridActionTarget[];
  moveTargets?: GridMoveTarget[];
}

// ============================================================================
// GRID COMMITTED ACTION (tile-based targeting)
// ============================================================================

export interface GridCommittedAction {
  actionId: string;
  actionType: CombatActionType;
  targetId?: string;
  targetPosition?: TilePosition;
  apSpent: number;
  resolveSpent?: number;
}

export interface GridRoundCommit {
  participantId: string;
  actions: GridCommittedAction[];
  reactions: CommittedReaction[];
}

// ============================================================================
// GRID COMBAT CONFIG
// ============================================================================

export interface GridPlayerDefinition {
  name: string;
  vitals: number;
  vitalsMax: number;
  resolve: number;
  resolveMax: number;
  stats: Stats;
  tags?: string[];
  equipment?: string[];
  magicDiscipline?: MagicDiscipline;
  skillIds?: string[];
  defense?: number;
  speed?: number;
  startX: number;
  startY: number;
}

export interface GridEnemyDefinition {
  name: string;
  race?: string;
  description?: string;
  vitals: number;
  vitalsMax?: number;
  resolve?: number;
  resolveMax?: number;
  tags: string[];
  defense?: number;
  speed?: number;
  startX: number;
  startY: number;
}

export interface GridCombatConfig {
  players: GridPlayerDefinition[];
  enemies: GridEnemyDefinition[];
  customSkills?: SkillDefinition[];
}

// ============================================================================
// INPUT TYPES
// ============================================================================

export type GridCombatInput =
  | { type: 'player_commit'; participantId: string; actions: GridCommittedAction[]; reactions?: CommittedReaction[] }
  | { type: 'reactions'; participantId: string; reactions: CommittedReaction[] }
  | { type: 'advance' }; // Auto-advance phases (player_resolve, enemy_turn, round_end)

// ============================================================================
// RE-EXPORTS for convenience
// ============================================================================

export type {
  APCombatParticipant,
  CommittedReaction,
  IncomingThreat,
  ResolutionResult,
  RoundResolution,
  RoundCommit,
  AvailableReactionSet,
  ReactionType,
  DicePoolPreview,
};
